import { supabase } from '../lib/supabase'
import type { Project, Bio, Service, PricingPackage, SocialLink, Resume, Content } from '../types/database'

// Projects
export async function getProjects(): Promise<Project[]> {
  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .eq('featured', true)
    .order('order', { ascending: true })

  if (error) {
    console.error('Error fetching projects:', error)
    return []
  }

  return data || []
}

export async function getProject(id: string): Promise<Project | null> {
  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .eq('id', id)
    .single()

  if (error) {
    console.error('Error fetching project:', error)
    return null
  }

  return data
}

export async function createProject(project: Omit<Project, 'id' | 'created_at' | 'updated_at'>): Promise<Project | null> {
  const { data, error } = await supabase
    .from('projects')
    .insert(project)
    .select()
    .single()

  if (error) {
    console.error('Error creating project:', error)
    return null
  }

  return data
}

export async function updateProject(id: string, project: Partial<Project>): Promise<Project | null> {
  const { data, error } = await supabase
    .from('projects')
    .update({ ...project, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single()

  if (error) {
    console.error('Error updating project:', error)
    return null
  }

  return data
}

export async function deleteProject(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('projects')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('Error deleting project:', error)
    return false
  }

  return true
}

// Bio
export async function getBio(): Promise<Bio | null> {
  const { data, error } = await supabase
    .from('bio')
    .select('*')
    .single()

  if (error) {
    console.error('Error fetching bio:', error)
    return null
  }

  return data
}

export async function updateBio(bio: Partial<Bio>): Promise<Bio | null> {
  // Assuming single bio record with id = 1
  const { data, error } = await supabase
    .from('bio')
    .upsert({ id: '1', ...bio, updated_at: new Date().toISOString() })
    .select()
    .single()

  if (error) {
    console.error('Error updating bio:', error)
    return null
  }

  return data
}

// Services
export async function getServices(category?: 'tutoring' | 'web-dev'): Promise<Service[]> {
  let query = supabase
    .from('services')
    .select('*')
  
  if (category) {
    query = query.eq('category', category)
  }
  
  query = query.order('order', { ascending: true })

  const { data, error } = await query

  if (error) {
    console.error('Error fetching services:', error)
    return []
  }

  return data || []
}

export async function getServicesByCategory(category: 'tutoring' | 'web-dev'): Promise<Service[]> {
  return getServices(category)
}

export async function createService(service: Omit<Service, 'id' | 'created_at' | 'updated_at'>): Promise<Service | null> {
  const { data, error } = await supabase
    .from('services')
    .insert(service)
    .select()
    .single()

  if (error) {
    console.error('Error creating service:', error)
    throw new Error(error.message || 'Failed to create service')
  }

  return data
}

export async function updateService(id: string, service: Partial<Service>): Promise<Service | null> {
  const { data, error } = await supabase
    .from('services')
    .update({ ...service, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single()

  if (error) {
    console.error('Error updating service:', error)
    throw new Error(error.message || 'Failed to update service')
  }

  return data
}

export async function deleteService(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('services')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('Error deleting service:', error)
    return false
  }

  return true
}

// Pricing Packages
export async function getPricingPackages(): Promise<PricingPackage[]> {
  const { data, error } = await supabase
    .from('pricing_packages')
    .select('*')
    .order('order', { ascending: true })

  if (error) {
    console.error('Error fetching pricing packages:', error)
    return []
  }

  return data || []
}

export async function createPricingPackage(pkg: Omit<PricingPackage, 'id' | 'created_at' | 'updated_at'>): Promise<PricingPackage | null> {
  const { data, error } = await supabase
    .from('pricing_packages')
    .insert(pkg)
    .select()
    .single()

  if (error) {
    console.error('Error creating pricing package:', error)
    return null
  }

  return data
}

export async function updatePricingPackage(id: string, pkg: Partial<PricingPackage>): Promise<PricingPackage | null> {
  const { data, error } = await supabase
    .from('pricing_packages')
    .update({ ...pkg, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single()

  if (error) {
    console.error('Error updating pricing package:', error)
    return null
  }

  return data
}

export async function deletePricingPackage(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('pricing_packages')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('Error deleting pricing package:', error)
    return false
  }

  return true
}

// Social Links
export async function getSocialLinks(): Promise<SocialLink[]> {
  const { data, error } = await supabase
    .from('social_links')
    .select('*')
    .order('order', { ascending: true })

  if (error) {
    console.error('Error fetching social links:', error)
    return []
  }

  return data || []
}

export async function createSocialLink(link: Omit<SocialLink, 'id' | 'created_at' | 'updated_at'>): Promise<SocialLink | null> {
  const { data, error } = await supabase
    .from('social_links')
    .insert(link)
    .select()
    .single()

  if (error) {
    console.error('Error creating social link:', error)
    return null
  }

  return data
}

export async function updateSocialLink(id: string, link: Partial<SocialLink>): Promise<SocialLink | null> {
  const { data, error } = await supabase
    .from('social_links')
    .update({ ...link, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single()

  if (error) {
    console.error('Error updating social link:', error)
    return null
  }

  return data
}

export async function deleteSocialLink(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('social_links')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('Error deleting social link:', error)
    return false
  }

  return true
}

// Resume
export async function getResume(): Promise<Resume | null> {
  const { data, error } = await supabase
    .from('resume')
    .select('*')
    .single()

  if (error) {
    console.error('Error fetching resume:', error)
    return null
  }

  return data
}

export async function updateResume(resume: Partial<Resume>): Promise<Resume | null> {
  const { data, error } = await supabase
    .from('resume')
    .upsert({ id: '1', ...resume, updated_at: new Date().toISOString() })
    .select()
    .single()

  if (error) {
    console.error('Error updating resume:', error)
    return null
  }

  return data
}

// Content (Code Example, Technologies, Quick Info)
export async function getContent(): Promise<Content | null> {
  try {
    const { data, error } = await supabase
      .from('content')
      .select('*')
      .single()

    if (error) {
      // If table doesn't exist (404), return null gracefully
      if (error.code === 'PGRST116' || error.message?.includes('does not exist') || error.message?.includes('404')) {
        console.warn('Content table does not exist, returning null')
        return null
      }
      console.error('Error fetching content:', error)
      return null
    }

    return data
  } catch (error) {
    console.error('Error fetching content:', error)
    return null
  }
}

export async function updateContent(content: Partial<Content>): Promise<Content | null> {
  const { data, error } = await supabase
    .from('content')
    .upsert({ id: '1', ...content, updated_at: new Date().toISOString() })
    .select()
    .single()

  if (error) {
    console.error('Error updating content:', error)
    return null
  }

  return data
}

// Leads
export interface Lead {
  id: string
  name: string
  email: string
  service_type: 'tutoring' | 'web-dev'
  subject?: string
  grade_level?: string
  biggest_struggle?: string
  free_resource: string
  status: 'new' | 'contacted' | 'converted' | 'nurturing'
  email_sent: boolean
  created_at: string
  updated_at: string
}

export async function createLead(lead: Omit<Lead, 'id' | 'created_at' | 'updated_at' | 'email_sent' | 'status'>): Promise<Lead | null> {
  const { data, error } = await supabase
    .from('leads')
    .insert({
      ...lead,
      status: 'new',
      email_sent: false,
    })
    .select()
    .single()

  if (error) {
    console.error('Error creating lead:', error)
    throw new Error(error.message || 'Failed to create lead')
  }

  return data
}

// Client Sessions
export interface ClientSession {
  id: string
  client_id: string
  booking_id?: string
  payment_id?: string
  service_type: 'tutoring' | 'web-dev'
  session_type?: string
  title: string
  description?: string
  scheduled_date?: string
  completed_date?: string
  duration_minutes?: number
  status: 'scheduled' | 'in-progress' | 'completed' | 'canceled'
  notes?: string
  progress_notes?: string
  next_steps?: string
  rating?: number
  feedback?: string
  metadata?: Record<string, any>
  created_at: string
  updated_at: string
}

export async function getSessions(filters?: {
  status?: string
  service_type?: string
  client_id?: string
  date_from?: string
  date_to?: string
}): Promise<ClientSession[]> {
  let query = supabase
    .from('client_sessions')
    .select('*, clients(name, email)')
    .order('scheduled_date', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false })

  if (filters?.status) {
    query = query.eq('status', filters.status)
  }
  if (filters?.service_type) {
    query = query.eq('service_type', filters.service_type)
  }
  if (filters?.client_id) {
    query = query.eq('client_id', filters.client_id)
  }
  if (filters?.date_from) {
    query = query.gte('scheduled_date', filters.date_from)
  }
  if (filters?.date_to) {
    query = query.lte('scheduled_date', filters.date_to)
  }

  const { data, error } = await query

  if (error) {
    console.error('Error fetching sessions:', error)
    return []
  }

  return data || []
}

export async function createSession(session: Omit<ClientSession, 'id' | 'created_at' | 'updated_at'>): Promise<ClientSession | null> {
  const { data, error } = await supabase
    .from('client_sessions')
    .insert(session)
    .select()
    .single()

  if (error) {
    console.error('Error creating session:', error)
    throw new Error(error.message || 'Failed to create session')
  }

  return data
}

export async function updateSession(id: string, session: Partial<ClientSession>): Promise<ClientSession | null> {
  const { data, error } = await supabase
    .from('client_sessions')
    .update({ ...session, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single()

  if (error) {
    console.error('Error updating session:', error)
    throw new Error(error.message || 'Failed to update session')
  }

  return data
}

export async function deleteSession(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('client_sessions')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('Error deleting session:', error)
    return false
  }

  return true
}

export async function bulkUpdateSessions(ids: string[], updates: Partial<ClientSession>): Promise<boolean> {
  const { error } = await supabase
    .from('client_sessions')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .in('id', ids)

  if (error) {
    console.error('Error bulk updating sessions:', error)
    return false
  }

  return true
}

// Bookings
export interface Booking {
  id: string
  client_id: string
  payment_id?: string
  service_type: 'tutoring' | 'web-dev'
  service_title?: string
  calendly_event_id?: string
  calendly_event_uri?: string
  calendly_invitee_uri?: string
  status: 'scheduled' | 'confirmed' | 'completed' | 'canceled' | 'no-show'
  scheduled_start: string
  scheduled_end: string
  timezone?: string
  location?: string
  meeting_url?: string
  notes?: string
  metadata?: Record<string, any>
  created_at: string
  updated_at: string
  canceled_at?: string
  completed_at?: string
}

export async function getBookings(filters?: {
  status?: string
  service_type?: string
  client_id?: string
  date_from?: string
  date_to?: string
}): Promise<Booking[]> {
  let query = supabase
    .from('bookings')
    .select('*, clients(name, email)')
    .order('scheduled_start', { ascending: false })

  if (filters?.status) {
    query = query.eq('status', filters.status)
  }
  if (filters?.service_type) {
    query = query.eq('service_type', filters.service_type)
  }
  if (filters?.client_id) {
    query = query.eq('client_id', filters.client_id)
  }
  if (filters?.date_from) {
    query = query.gte('scheduled_start', filters.date_from)
  }
  if (filters?.date_to) {
    query = query.lte('scheduled_start', filters.date_to)
  }

  const { data, error } = await query

  if (error) {
    console.error('Error fetching bookings:', error)
    return []
  }

  return data || []
}

export async function createBooking(booking: Omit<Booking, 'id' | 'created_at' | 'updated_at'>): Promise<Booking | null> {
  const { data, error } = await supabase
    .from('bookings')
    .insert(booking)
    .select()
    .single()

  if (error) {
    console.error('Error creating booking:', error)
    throw new Error(error.message || 'Failed to create booking')
  }

  return data
}

export async function updateBooking(id: string, booking: Partial<Booking>): Promise<Booking | null> {
  const { data, error } = await supabase
    .from('bookings')
    .update({ ...booking, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single()

  if (error) {
    console.error('Error updating booking:', error)
    throw new Error(error.message || 'Failed to update booking')
  }

  return data
}

export async function cancelBooking(id: string): Promise<boolean> {
  // First get the booking to check if we need to refund credits
  const { data: booking, error: fetchError } = await supabase
    .from('bookings')
    .select('*, payments(metadata)')
    .eq('id', id)
    .single()

  if (fetchError || !booking) {
    console.error('Error fetching booking:', fetchError)
    // Still try to cancel even if we can't fetch payment info
    const { error: updateError } = await supabase
      .from('bookings')
      .update({
        status: 'canceled',
        canceled_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
    return !updateError
  }

  // Update booking status
  const { error: updateError } = await supabase
    .from('bookings')
    .update({
      status: 'canceled',
      canceled_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)

  if (updateError) {
    console.error('Error canceling booking:', updateError)
    return false
  }

  // Refund session credit if applicable
  if (booking.payment_id && booking.metadata?.sessionsDeducted) {
    const payment = Array.isArray(booking.payments) ? booking.payments[0] : booking.payments
    if (payment?.metadata) {
      const sessionsToRefund = booking.metadata.sessionsDeducted || 1
      const currentSessions = payment.metadata.sessionsRemaining || 0
      const newSessionsRemaining = currentSessions + sessionsToRefund

      await supabase
        .from('payments')
        .update({
          metadata: {
            ...payment.metadata,
            sessionsRemaining: newSessionsRemaining,
          },
          updated_at: new Date().toISOString(),
        })
        .eq('id', booking.payment_id)
    }
  }

  return true
}

export async function deleteBooking(id: string): Promise<boolean> {
  // Similar to cancel, refund credits first
  const { data: booking } = await supabase
    .from('bookings')
    .select('*, payments(metadata)')
    .eq('id', id)
    .single()

  if (booking?.payment_id && booking.metadata?.sessionsDeducted) {
    const payment = Array.isArray(booking.payments) ? booking.payments[0] : booking.payments
    if (payment?.metadata) {
      const sessionsToRefund = booking.metadata.sessionsDeducted || 1
      const currentSessions = payment.metadata.sessionsRemaining || 0
      const newSessionsRemaining = currentSessions + sessionsToRefund

      await supabase
        .from('payments')
        .update({
          metadata: {
            ...payment.metadata,
            sessionsRemaining: newSessionsRemaining,
          },
          updated_at: new Date().toISOString(),
        })
        .eq('id', booking.payment_id)
    }
  }

  const { error } = await supabase
    .from('bookings')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('Error deleting booking:', error)
    return false
  }

  return true
}

export async function bulkUpdateBookings(ids: string[], updates: Partial<Booking>): Promise<boolean> {
  const { error } = await supabase
    .from('bookings')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .in('id', ids)

  if (error) {
    console.error('Error bulk updating bookings:', error)
    return false
  }

  return true
}

// Clients/Students
export interface Client {
  id: string
  name?: string // Kept for backward compatibility
  first_name?: string
  last_name?: string
  email: string
  phone?: string
  user_id?: string
  service_preferences?: Record<string, any>
  notes?: string
  status: 'active' | 'inactive' | 'archived'
  created_at: string
  updated_at: string
}

/**
 * Get display name from client object
 * Prefers first_name + last_name, falls back to name, then email prefix
 */
export function getClientDisplayName(client: Client | { name?: string; first_name?: string; last_name?: string; email: string }): string {
  if (client.first_name && client.last_name) {
    return `${client.first_name} ${client.last_name}`;
  }
  if (client.first_name) return client.first_name;
  if (client.last_name) return client.last_name;
  if (client.name) return client.name;
  return client.email.split('@')[0];
}

export async function getClients(filters?: {
  status?: string
  search?: string
}): Promise<Client[]> {
  let query = supabase
    .from('clients')
    .select('*')
    .order('created_at', { ascending: false })

  if (filters?.status) {
    query = query.eq('status', filters.status)
  }
  if (filters?.search) {
    query = query.or(`name.ilike.%${filters.search}%,first_name.ilike.%${filters.search}%,last_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%,phone.ilike.%${filters.search}%`)
  }

  const { data, error } = await query

  if (error) {
    console.error('Error fetching clients:', error)
    return []
  }

  return data || []
}

export async function createClient(client: Omit<Client, 'id' | 'created_at' | 'updated_at'>): Promise<Client | null> {
  const { data, error } = await supabase
    .from('clients')
    .insert(client)
    .select()
    .single()

  if (error) {
    console.error('Error creating client:', error)
    throw new Error(error.message || 'Failed to create client')
  }

  return data
}

export async function updateClient(id: string, client: Partial<Client>): Promise<Client | null> {
  const { data, error } = await supabase
    .from('clients')
    .update({ ...client, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single()

  if (error) {
    console.error('Error updating client:', error)
    throw new Error(error.message || 'Failed to update client')
  }

  return data
}

export async function deleteClient(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('clients')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('Error deleting client:', error)
    return false
  }

  return true
}

export async function bulkUpdateClients(ids: string[], updates: Partial<Client>): Promise<boolean> {
  const { error } = await supabase
    .from('clients')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .in('id', ids)

  if (error) {
    console.error('Error bulk updating clients:', error)
    return false
  }

  return true
}


